import React, { useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import useRefreshLocationData, {
  RefreshType,
} from "../../../containers/FacilityDetailsPage/hooks/useRefreshLocationData";
import { LocationData } from "../../../models/LocationData";
import DataSvc from "../../../services/dataSvc";
import { createNestedFormData } from "../../../utils/formData";
import { ModalPleaseWait } from "../../ModalPleaseWait";
import { CreateLocalPostForm } from "../CreateLocalPostForm";
import { CreateLocalPostState } from "../CreateLocalPostForm/utils";
import { ModalLocalPostIdeas } from "../ModalLocalPostIdeas";

import "./styles.scss";

export interface IModalCreateLocalPostProps {
  locationData: LocationData;
  locationId: string;
  onClose: () => void;
  onSchedulePostSuccess?: () => void;
  onPostSuccess?: () => void;
}

export const ModalCreateLocalPost: React.FunctionComponent<
  IModalCreateLocalPostProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`analysisCollectionsPage.modalCreateLocalPost.${key}`);

  const {
    locationData,
    onClose,
    locationId,
    onSchedulePostSuccess,
    onPostSuccess,
  } = props;

  const [shownModalLocalPostIdeas, setShownModalLocalPostIdeas] =
    useState<boolean>(false);

  const [shownModalPleaseWait, setShownModalPleaseWait] =
    useState<boolean>(false);

  const refresh = useRefreshLocationData(locationId);

  const post = async (
    value: CreateLocalPostState,
    postScheduleType: "immediate" | "schedule",
    scheduleAt?: string
  ) => {
    const { media, ...data } = value;
    const formData = createNestedFormData(data);
    media.forEach((_media) => {
      formData.append("media", _media);
    });
    if (postScheduleType === "schedule" && scheduleAt) {
      formData.append("scheduleAt", scheduleAt);
    }
    ReactGA.event("Local Post Posted", {
      category: "create-local-post",
      label: data.topicType,
      hasMedia: media.length > 0,
      hasText: data.text.length > 0,
      callToActionType: data.callToAction.actionType,
    });
    setShownModalPleaseWait(true);
    try {
      let response;
      let postToGbpStatus: "success" | "failure" | undefined = undefined;
      let postToInstagramStatus: "success" | "failure" | undefined = undefined;

      if (postScheduleType === "schedule") {
        response = await DataSvc.createSchedulePost(locationId, formData);
        toast.success(t("schedule_post_success"));
        if (onSchedulePostSuccess) {
          onSchedulePostSuccess();
        }
      } else {
        response = await DataSvc.createLocalPost(locationId, formData);

        if (!!data.postToGbp) {
          postToGbpStatus =
            response.data.gbpPost === undefined ? "failure" : "success";
        }

        if (!!data.postToInstagram) {
          postToInstagramStatus =
            response.data.instagramPost === undefined ? "failure" : "success";
        }

        if (
          postToGbpStatus === "success" &&
          postToInstagramStatus === "success"
        ) {
          toast.success(t("all_success"));
        } else {
          if (postToGbpStatus === "failure") {
            toast.error(t("gbp_failure"));
          } else if (postToGbpStatus === "success") {
            toast.success(t("gbp_success"));
          }

          if (postToInstagramStatus === "failure") {
            toast.error(t("instagram_failure"));
          } else if (postToInstagramStatus === "success") {
            toast.success(t("instagram_success"));
          }
        }

        if (onPostSuccess) {
          onPostSuccess();
        }
      }
      refresh(RefreshType.LOCATION, RefreshType.LOCAL_POSTS);

      // Close the modal if there were no failures
      if (
        postToGbpStatus !== "failure" &&
        postToInstagramStatus !== "failure"
      ) {
        onClose();
      }
    } catch (error) {
      // Do nothing. Axios should handle displaying the error automatically.
    } finally {
      setShownModalPleaseWait(false);
    }
  };

  return (
    <div className="modal modal-default modal-create-local-post">
      <div className="modal-mains">
        <button
          className="btn-close"
          onClick={() => {
            onClose();
          }}
        ></button>
        <div className="modal-mains__body">
          <div className="top-title">
            <h2>{t("new_post")}</h2>
            <button
              className="btn btn-border"
              onClick={() => setShownModalLocalPostIdeas(true)}
            >
              {t("get_ideas")}
            </button>
          </div>
          <CreateLocalPostForm
            locationData={locationData}
            post={post}
            onClose={onClose}
            canGenerateAiText
            enablePostToInstagram
            locationNames={[locationData.name]}
            showPostScheduleTypeRadio={true}
          />
        </div>
      </div>
      {shownModalLocalPostIdeas && (
        <ModalLocalPostIdeas
          locationData={locationData}
          onClose={() => setShownModalLocalPostIdeas(false)}
        />
      )}
      {shownModalPleaseWait && (
        <ModalPleaseWait description={t("please_wait_message")} />
      )}
    </div>
  );
};
